import { Plus } from 'lucide-react'
import { useState } from 'react'
import { PopoverSheet } from '@/components/ui/PopoverSheet'
import type { PickerData } from '@/lib/types'
import { cn } from '@/lib/utils'
import { EntryPicker } from './EntryPicker'
import type { NewRowSelection } from './week-rows'

/**
 * De vorm van een icoonknop in de actiekolom van een regel. Op touch bestaat hover niet,
 * dus daar staan de knoppen gewoon aan. Woont hier omdat `RowActions` deze knop rendert
 * en dus toch al deze kant op importeert.
 */
export const rowActionButtonClass =
  'rounded-sm p-1 text-ink-faint transition-colors duration-150 hover:bg-fill-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40 disabled:cursor-not-allowed md:opacity-0 md:focus:opacity-100 md:group-hover:opacity-100 md:group-focus-within:opacity-100'

const LABEL = 'Regel toevoegen'

const variantClass = {
  hero: 'btn-primary',
  inline:
    'flex w-full items-center justify-center gap-1.5 rounded-card border border-dashed border-hairline px-3 py-2.5 text-sm font-medium text-accent-700 transition-colors duration-150 hover:bg-fill-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
  icon: `${rowActionButtonClass} hover:text-accent-700`,
} as const

interface AddRowButtonProps {
  pickers: PickerData
  onAdd: (selection: NewRowSelection) => void
  /**
   * `hero` staat in een lege week midden op het scherm, `inline` onder een gevulde lijst,
   * `icon` in de actiekolom van een regel.
   */
  variant?: keyof typeof variantClass
  className?: string
}

/**
 * De weg naar een nieuwe regel: kies een klant, dan een project, dan een categorie. Zowel
 * de knop onder de lijst als de plus op een regel komen hier uit, want het is dezelfde
 * handeling; alleen de knop eromheen verschilt.
 */
export function AddRowButton({ pickers, onAdd, variant = 'inline', className }: AddRowButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <PopoverSheet
      open={open}
      onOpenChange={setOpen}
      title={LABEL}
      className="w-88"
      align={variant === 'icon' ? 'end' : 'start'}
      trigger={
        <button
          type="button"
          aria-label={LABEL}
          title={variant === 'icon' ? LABEL : undefined}
          className={cn(variantClass[variant], className)}
        >
          <Plus className="h-4 w-4" />
          {variant === 'icon' ? null : LABEL}
        </button>
      }
    >
      <EntryPicker
        pickers={pickers}
        onPick={(selection) => {
          onAdd(selection)
          setOpen(false)
        }}
      />
    </PopoverSheet>
  )
}
